import { createRouter, createWebHistory, type LocationQuery, type LocationQueryValue, type RouteRecordName } from 'vue-router';
import routes from './routes';
import { isAuthenticated } from '@/stores/authStore';
import { useUserStore } from '@/stores/userStore';
import { useMainStore } from '@/stores/mainStore';
import { isImpersonating, useAuthStore } from '@/stores/authStore';
import type { ProfileAccessService } from '@/types/users';
import type { ApiV1, PermissionRule } from '@teamworksdev/influencer/core';
import { useAuthService } from '@/api/authService';
import { useBasicAuth } from '@/composables/useBasicAuth';
import { isEmpty } from 'lodash-es';
import { add } from 'date-fns';
import type { RouteMeta } from 'vue-router';
import { normalizeToArray } from '@/utils/helpers';
import qs from 'qs';
import { updateLDContext } from '@/plugins/launchdarkly';
import { checkPermission } from '@/utils/permissions';

declare module 'vue-router' {
	interface RouteMeta {
		layout: 'LayoutLogin' | 'LayoutAuthenticated' | 'LayoutDefault' | 'LayoutExchangeSchoolSite';
		layoutOptions?: Record<string, unknown>;
		title?: string;
		requiresAuth: boolean;
		requiredAccess?: keyof ProfileAccessService | (() => boolean) | (keyof ProfileAccessService)[];
		profileSwitchRedirect?: RouteRecordName | (() => RouteRecordName);
		requiredLDFlags?: string[];
		permissionRule?: PermissionRule;
	}
}

type DefaultProfileResult = {
	hasDefaultProfile: boolean;
	requiresTOU: boolean;
} | null;

let defaultProfileResult: DefaultProfileResult = null;

const router = createRouter({
	history: createWebHistory(import.meta.env.BASE_URL),
	routes,
	scrollBehavior(to) {
		if (to.hash) {
			return new Promise((resolve) => {
				setTimeout(() => {
					resolve({
						el: to.hash,
						behavior: 'smooth',
					});
				}, 500);
			});
		}
	},
	parseQuery: (query) => qs.parse(query, { decoder: (str, decoder, charset) => {
		const strWithoutPlus = str.replace(/\+/g, ' ');
		if (charset === 'iso-8859-1') {
			return strWithoutPlus.replace(/%[0-9a-f]{2}/gi, function (match) {
				try {
					return decodeURIComponent(match);
				} catch {
					return match;
				}
			});
		}

		if (/^(\d+|\d*\.\d+)$/.test(str)) {
			return parseFloat(str);
		}

		const keywords: { [key: string]: boolean | null | undefined } = {
			true: true,
			false: false,
			null: null,
			undefined: undefined,
		};
		if (str in keywords) {
			return keywords[str];
		}

		// utf-8
		try {
			return decodeURIComponent(strWithoutPlus);
		} catch {
			return strWithoutPlus;
		}
	} }) as LocationQuery,
});

async function hasUser(): Promise<boolean> {
	const userStore = useUserStore();
	if (userStore.user) return true;

	const mainStore = useMainStore();
	mainStore.isAppLoading = true;

	try {
		const result = await userStore.setUserWithDefaultProfile();
		defaultProfileResult = result;
		return true;
	} catch (e) {
		console.error(e);
		return false;
	}
}

async function hasProfile(): Promise<boolean> {
	const userStore = useUserStore();
	if (userStore.profile) return true;

	const mainStore = useMainStore();
	mainStore.isAppLoading = true;

	try {
		await userStore.setProfile();
		return true;
	} catch {
		return false;
	}
}

async function hasImpersonatedByUser(): Promise<boolean> {
	if (!isImpersonating()) return true;

	const userStore = useUserStore();

	if (userStore.impersonatedByProfile) return true;

	const mainStore = useMainStore();
	mainStore.isAppLoading = true;

	try {
		await userStore.refreshImpersonatedByUser();
		if (!userStore.impersonatedByProfile) throw new Error();
		return true;
	} catch {
		return false;
	}
}

function resolveRequiredAccess(requiredAccess: RouteMeta['requiredAccess']): boolean {
	const userStore = useUserStore();
	if (!requiredAccess) return true;
	return typeof requiredAccess === 'function' ? requiredAccess() : normalizeToArray(requiredAccess).some((k) => userStore.profileAccess[k]);
}

function resolvePermissionRule(rule: PermissionRule): boolean {
	const userStore = useUserStore();
	if (!userStore.profile) return false;

	try {
		return checkPermission(userStore.profile, rule);
	} catch (error) {
		console.error('[Router] Permission check failed:', error);
		return false;
	}
}

async function resolveLdFlagAccess(flags: string[]): Promise<boolean> {
	const ldClient = await updateLDContext();
	if (!ldClient) return false;

	for (const flag of flags) {
		if (ldClient.variation(flag, false) === false) {
			return false;
		};
	}
	return true;
}


type CanAccessRouteArgs = Pick<RouteMeta, 'permissionRule' | 'requiredAccess' | 'requiredLDFlags'>;

export async function canAccessRoute(params: CanAccessRouteArgs): Promise<boolean> {
	const { permissionRule, requiredAccess, requiredLDFlags } = params;

	if (permissionRule && !resolvePermissionRule(permissionRule)) return false;
	if (requiredAccess && !resolveRequiredAccess(requiredAccess)) return false;
	if (requiredLDFlags && !(await resolveLdFlagAccess(requiredLDFlags))) return false;
	return true;
}

async function setAuthTokenFromQuery(token: LocationQueryValue | LocationQueryValue[]) {
	if (!token || Array.isArray(token)) return false;
	const { introspectToken } = useAuthService();
	const authStore = useAuthStore();

	const authToken: ApiV1.AuthToken = {
		uuid: crypto.randomUUID(),
		access_token: token,
		expires_at: add(new Date(), { days: 1 }).toISOString(),
	};

	authStore.setToken(authToken, true);

	try {
		const _token = await introspectToken();
		authStore.setToken(_token, true);
		return true;
	} catch (error) {
		console.error(error);
		return false;
	}
}

function setInflcrId(profileUuid: LocationQueryValue | LocationQueryValue[]) {
	if (!profileUuid || Array.isArray(profileUuid)) return false;
	const userStore = useUserStore();
	userStore.setInflcrId(profileUuid as string);
}

router.beforeEach(async (to, from) => {
	const userStore = useUserStore();

	if (
		to.query.token
		&& to.name !== 'LoginAxle'
		&& to.name !== 'LoginResetPassword'
	) await setAuthTokenFromQuery(to.query.token);

	if (to.query.profileUuid) setInflcrId(to.query.profileUuid);

	if (to.meta.requiresAuth || to.path === '/login') {
		if (!isAuthenticated() || !await hasUser() || !await hasImpersonatedByUser()) {
			if (to.path === '/login') return;

			const { logOut } = useBasicAuth();
			await logOut({ redirect: false });
			const redirect = window.location.pathname;
			return { name: 'LoginIndex', query: { redirect } };
		}

		// Check if default profile was loaded
		const result = defaultProfileResult;
		const hasDefaultProfile = result?.hasDefaultProfile;
		const requiresTOU = result?.requiresTOU;

		// Clear temp result
		defaultProfileResult = null;

		// If default profile loaded and TOU accepted, skip profile chooser
		if (hasDefaultProfile && !requiresTOU && to.name === 'ProfileChooserIndex') {
			const redirect = to.query.redirect as string;
			return redirect ? { path: redirect } : { name: userStore.profileDefaultRoute };
		}

		if (to.name !== 'ProfileChooserIndex' && !(await hasProfile())) {
			return { name: 'ProfileChooserIndex' };
		}

		if (to.name === 'ProfileChooserIndex' && (await hasProfile())) {
			return from.name && from.meta.requiresAuth ? false : { name: userStore.profileDefaultRoute };
		}

		if (to.meta.requiredAccess || to.meta.permissionRule || to.meta.requiredLDFlags) {
			if (!(await canAccessRoute(to.meta))) {
				return { name: 'NoAccess' };
			}
		}
	}
});

router.afterEach((to, from) => {
	if (
		isEmpty(from.name)
		&& to.name !== 'LoginAxle'
		&& to.name !== 'LoginResetPassword'
		&& (to.query.token || to.query.profileUuid)
	) router.replace({
		query: {
			...to.query,
			token: undefined,
			profileUuid: undefined,
		},
	});

	const mainStore = useMainStore();
	mainStore.isAppLoading = false;
});

router.onError((error, to) => {
	if (error.message.includes('Failed to fetch dynamically imported module') || error.message.includes('Failed to load module script')) {
		(window as Window).location = to.fullPath;
	}
});

export default router;
